import PropTypes from 'prop-types';
import { flexRender } from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '#/lib/utils';
import { renderEntityStatusBadge } from '#/modules/commonComponents/table/entityStatusIconHelpers';
import {
  DEFAULT_TABLE_TEXT_SIZE,
  tableTextSizeClassName,
} from '#/modules/commonComponents/table/tableTextSize';
import { TooltipProvider, DEFAULT_TOOLTIP_DELAY_MS } from '#/modules/commonComponents/tooltip';

function renderSortIndicator(isSorted) {
  if (isSorted === 'asc') {
    return <ChevronUp size={14} className="text-primary-600" />;
  }
  if (isSorted === 'desc') {
    return <ChevronDown size={14} className="text-primary-600" />;
  }
  return null;
}

const DetailTable = ({
  state,
  loading = false,
  containerClassName = '',
  tableClassName = '',
  textSize = DEFAULT_TABLE_TEXT_SIZE,
  getRowHighlight,
  onRowClick,
}) => {
  const { table } = state;

  return (
    <TooltipProvider delayDuration={DEFAULT_TOOLTIP_DELAY_MS}>
      <div className={cn('detail-table-scroll', containerClassName)}>
        {loading ? (
          <div className="cover_overlay flex items-center justify-center bg-gray-400/65">
            <span className="text-sm text-gray-700">載入中...</span>
          </div>
        ) : null}
        <table
          className={cn('detail-table', tableTextSizeClassName(textSize), tableClassName)}
          style={{ minWidth: table.getCenterTotalSize() }}
        >
          <thead className="sticky top-0 z-10 shadow-sm shadow-gray-300/65">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const canResize = header.column.getCanResize();
                  const isResizing = header.column.getIsResizing();
                  const sortHandler = canSort
                    ? header.column.getToggleSortingHandler()
                    : undefined;

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'relative',
                        canSort && 'cursor-pointer shortcut_transition-colors hover:bg-primary-200',
                        isResizing && 'th_resizing',
                        header.column.columnDef.meta?.headerClassName,
                      )}
                      style={{ width: header.getSize() }}
                      onClick={sortHandler}
                    >
                      {header.isPlaceholder ? null : (
                        <span className="inline-flex items-center justify-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {renderSortIndicator(header.column.getIsSorted())}
                        </span>
                      )}
                      {canResize ? (
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label="調整欄寬"
                          className="th_resize_handle"
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={(event) => event.stopPropagation()}
                          onDoubleClick={(event) => {
                            event.stopPropagation();
                            header.column.resetSize();
                          }}
                        />
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex, rows) => {
              const isLastSubRowOfLastGroup = row.depth > 0
                && rowIndex === rows.length - 1;

              return (
                <tr
                  key={row.id}
                  className={cn(
                    row.depth === 0 ? 'detail-table__parent-row' : 'detail-table__subrow',
                    isLastSubRowOfLastGroup && 'detail-table__subrow--last',
                    (getRowHighlight ? getRowHighlight(row) : row.getIsSelected()) && 'table_row_selected',
                    onRowClick && 'cursor-pointer',
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const useStatusIcon = cell.column.id === 'status'
                      && cell.column.columnDef.meta?.statusPresentation !== 'badge';

                    if (useStatusIcon) {
                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            !cell.column.columnDef.meta?.noTruncate && 'truncate',
                            cell.column.columnDef.meta?.cellClassName,
                          )}
                          style={{
                            width: cell.column.getSize(),
                          }}
                        >
                          {renderEntityStatusBadge(cell.getValue())}
                        </td>
                      );
                    }

                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          !cell.column.columnDef.meta?.noTruncate && 'truncate',
                          cell.column.columnDef.meta?.cellClassName,
                        )}
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {cell.column.columnDef.cell
                          ? flexRender(cell.column.columnDef.cell, cell.getContext())
                          : cell.getValue()}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
};

DetailTable.propTypes = {
  state: PropTypes.shape({
    table: PropTypes.object.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  containerClassName: PropTypes.string,
  tableClassName: PropTypes.string,
  textSize: PropTypes.oneOf(['sm', 'base']),
  getRowHighlight: PropTypes.func,
  onRowClick: PropTypes.func,
};

export default DetailTable;

export function DetailTableExpanderCell({ row }) {
  if (!row.getCanExpand()) {
    return <span className="inline-block w-5" aria-hidden="true" />;
  }

  return (
    <button
      type="button"
      className="
        text-gray-500 inline-flex h-5 w-5 items-center justify-center border-0 bg-transparent p-0
        hover:text-primary-600
      "
      aria-label={row.getIsExpanded() ? '收合明細' : '展開明細'}
      onClick={(event) => {
        event.stopPropagation();
        row.getToggleExpandedHandler()(event);
      }}
    >
      <ChevronDown
        size={16}
        className={cn('shortcut_transition-transform', !row.getIsExpanded() && '-rotate-90')}
      />
    </button>
  );
}

DetailTableExpanderCell.propTypes = {
  row: PropTypes.object.isRequired,
};
